<template>
  <Popup
    class="confirm-dialog__wrapper"
    v-model:show="visible"
  >
    <div class="confirm-dialog">
      <img
        @click="handleClose"
        class="confirm-dialog__close"
        src="@/assets/images/close-icon.svg"
        alt=""
      >

      <div class="confirm-dialog__content">
        <div class="confirm-dialog__title">
          {{ title }}
        </div>
        {{ content }}
      </div>

      <div class="confirm-dialog__footer">
        <Button
          v-if="showCancelButton"
          @click="handleCancel"
          class="confirm-dialog__cancel"
        >
          {{ cancelText }}
        </Button>
        <Button
          @click="handleConfirm"
          class="confirm-dialog__confirm"
          type="primary"
        >
          {{ confirmText }}
        </Button>
      </div>
    </div>
  </Popup>
</template>

<script setup>
import { Button } from '@/components/Button'
import { Popup } from '@/components/Popup'

const props = defineProps({
  title: {
    type: String,
    default: 'Title'
  },
  content: {
    type: String,
    default: 'Content'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  closeOnClickModal: {
    type: Boolean,
    default: true
  },
  closeOnPressEscape: {
    type: Boolean,
    default: true
  },
  showCancelButton: {
    type: Boolean,
    default: true
  }
})

const emits = defineEmits(['confirm', 'cancel', 'close'])

const visible = defineModel({ required: true })

const handleClose = () => {
  visible.value = false
  emits('close')
}

const handleCancel = () => {
  emits('cancel')
  handleClose()
}

const handleConfirm = () => {
  emits('confirm')
  handleClose()
}

const handleKeydown = (e) => {
  if (e.key === 'Escape' && visible.value && props.closeOnPressEscape) {
    handleClose()
  }
}

// 监听 visible 变化
watch(visible, (newVal) => {
  if (newVal) {
    // 对话框打开时
    document.addEventListener('keydown', handleKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    // 对话框关闭时
    document.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
  }
}, { immediate: true })

onMounted(() => {
  visible.value = true
})

// 组件卸载时清理
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<style lang='less'>
.confirm-dialog {
  position: relative;
  background-color: white;
  width: 100%;

  &__wrapper {
    padding: 0 24px;
    width: 100%;
    max-width: 424px;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;

    @media (min-width: 768px) {
      top: 16px;
      right: 16px;
      width: 28px;
      height: 28px;
    }
  }

  &__content {
    padding: 16px 12px 24px;
    font-family: mon-600;
    font-size: 12px;
    line-height: 16px;

    @media (min-width: 768px) {
      padding: 24px 32px;
    }
  }

  &__title {
    margin-bottom: 8px;
    font-family: mon-700;
    font-size: 16px;
    line-height: 24px;

    @media (min-width: 768px) {
      margin-bottom: 12px;
    }
  }

  &__footer {
    display: flex;
    gap: 11px;
    padding: 11px 12px 16px;
    border-top: 1px solid #EBF1F5;

    @media (min-width: 768px) {
      gap: 16px;
      padding: 20px 24px 24px;
    }
  }
}
</style>
